import React, { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

interface FAQItem {
  question: string;
  answer: string;
  category: string;
}

const faqData: FAQItem[] = [
  {
    question: "How do I create an account?",
    answer: "To create an account, click the 'Sign Up' button on the homepage. You'll need to provide your email address, create a password, and verify your email address. Once verified, you can start using all features of the platform.",
    category: "Account"
  },
  {
    question: "How do I reset my password?",
    answer: "If you've forgotten your password, click 'Forgot Password' on the login page. Enter your email address and we'll send you a link to reset your password. Make sure to check your spam folder if you don't see the email.",
    category: "Account"
  },
  {
    question: "What subscription plans are available?",
    answer: "We offer several subscription tiers: Free (basic features), Pro (advanced features), and Enterprise (custom solutions). Each plan includes different levels of access to our premium features and support.",
    category: "Billing"
  },
  {
    question: "How do I cancel my subscription?",
    answer: "You can cancel your subscription at any time from your account settings. Go to 'Billing' in your account dashboard and click 'Cancel Subscription'. Your access will continue until the end of your current billing period.",
    category: "Billing"
  },
  {
    question: "What payment methods do you accept?",
    answer: "We accept all major credit cards (Visa, MasterCard, American Express), PayPal, and bank transfers for annual plans. All payments are processed securely through our payment partners.",
    category: "Billing"
  },
  {
    question: "How do I contact customer support?",
    answer: "You can reach our customer support team through multiple channels: email at support@saunadip.com, live chat (available during business hours), or by submitting a ticket through our help center. We typically respond within 24 hours.",
    category: "Support"
  },
  {
    question: "What are your business hours?",
    answer: "Our customer support team is available Monday through Friday, 9 AM to 6 PM EST. For urgent issues outside of business hours, please email us and we'll respond as soon as possible.",
    category: "Support"
  },
  {
    question: "How do I update my profile information?",
    answer: "To update your profile, go to 'Account Settings' in your dashboard. You can modify your personal information, profile picture, and preferences. Changes are saved automatically.",
    category: "Account"
  },
  {
    question: "Is my data secure?",
    answer: "Yes, we take data security seriously. We use industry-standard encryption, regular security audits, and comply with data protection regulations. Your personal information is never shared with third parties without your consent.",
    category: "Security"
  },
  {
    question: "Can I export my data?",
    answer: "Yes, you can export your data at any time. Go to 'Account Settings' and look for the 'Data Export' option. You'll receive a download link with all your data in a standard format.",
    category: "Account"
  },
  {
    question: "What browsers are supported?",
    answer: "We support all modern browsers including Chrome, Firefox, Safari, and Edge. For the best experience, we recommend using the latest version of your preferred browser.",
    category: "Technical"
  },
  {
    question: "Do you offer mobile apps?",
    answer: "Currently, our platform is optimized for mobile browsers. We're working on native mobile apps for iOS and Android, which will be available soon. The mobile web version provides full functionality.",
    category: "Technical"
  }
];

const categories = ['All', 'Account', 'Billing', 'Support', 'Security', 'Technical'];

const FAQ: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [openItems, setOpenItems] = useState<Set<number>>(new Set());

  const filteredFAQ = selectedCategory === 'All' 
    ? faqData 
    : faqData.filter(item => item.category === selectedCategory);

  const toggleItem = (index: number) => {
    const newOpenItems = new Set(openItems);
    if (newOpenItems.has(index)) {
      newOpenItems.delete(index);
    } else {
      newOpenItems.add(index);
    }
    setOpenItems(newOpenItems);
  };

  return (
    <>
      <Helmet>
        <title>FAQ - Frequently Asked Questions | SaunaDip</title>
        <meta name="description" content="Find answers to common questions about SaunaDip. Get help with account creation, billing, support, and more." />
        <meta name="keywords" content="FAQ, help, support, sauna, cold plunge, wellness, directory" />
        <link rel="canonical" href="/faq" />
      </Helmet>
      
      <div className="min-h-screen bg-sauna-beige py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-sauna-navy mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-700 max-w-2xl mx-auto">
              Find answers to common questions about our platform. Can't find what you're looking for? 
              <a href="/contact" className="text-sauna-orange hover:text-sauna-orange/80 font-medium ml-1">
                Contact our support team
              </a>
            </p>
          </div>

          {/* Category Filter */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-2 justify-center">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedCategory === category
                      ? 'bg-sauna-orange text-white'
                      : 'bg-white text-gray-800 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          {/* FAQ Items */}
          <div className="space-y-4">
            {filteredFAQ.map((item, index) => (
              <div
                key={index}
                className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => toggleItem(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-sauna-orange/5 transition-colors"
                >
                  <span className="text-lg font-medium text-gray-800 pr-4">
                    {item.question}
                  </span>
                  {openItems.has(index) ? (
                    <ChevronDown className="h-5 w-5 text-sauna-orange flex-shrink-0" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-sauna-orange flex-shrink-0" />
                  )}
                </button>
                {openItems.has(index) && (
                  <div className="px-6 pb-4">
                    <p className="text-gray-700 leading-relaxed">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Contact Section */}
          <div className="mt-12 bg-white rounded-xl shadow border border-gray-200 p-8 text-center">
            <h2 className="text-2xl font-bold text-sauna-navy mb-4">
              Still need help?
            </h2>
            <p className="text-gray-700 mb-6 max-w-md mx-auto">
              Our support team is here to help you with any questions or issues you might have.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-sauna-orange hover:bg-sauna-orange/90 transition-colors shadow"
              >
                Contact Support
              </a>
              <a
                href="/documentation"
                className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-800 bg-white hover:bg-gray-100 transition-colors shadow"
              >
                View Documentation
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default FAQ; 